// O store entra primeiro: slices -> services -> lib/logs -> store forma um ciclo, e se outro
// modulo puxar um slice antes, o configureStore roda com middlewares ainda undefined.
import { store } from "./redux/store.ts";
import { FlagProvider } from "@unleash/proxy-client-react";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router";
import { App } from "./App.tsx";
import { Toaster } from "./components/ui/sonner.tsx";
import { TooltipProvider } from "./components/ui/tooltip.tsx";
import "./index.css";
import { i18nPronto } from "./lib/i18n.ts";
import "./lib/zod-error-map.ts";
import "./lib/performance-utils.ts";
import { PerformanceProvider } from "./contexts/PerformanceContext.tsx";
import { SheetStackProvider } from "./components/ui/sheet-stack-context.tsx";
import { LeadWhatsappHost } from "./components/lead-whatsapp/LeadWhatsappHost.tsx";
import { iniciarWebVitals } from "./lib/web-vitals.ts";
import { iniciarTrackingNovaVersao } from "./lib/nova-versao-tracking.ts";
import { instalarHandlerPreloadError } from "./lib/lazy-with-retry.ts";

// Chunk da build anterior (404 pos-deploy): recarrega uma vez.
instalarHandlerPreloadError();

if (import.meta.env.DEV) (window as any).__REDUX_STORE__ = store;

const flagProvider = {
  url: "https://unleash.sysigreja.com/api/frontend",
  clientKey: import.meta.env.VITE_UNLEASH_KEY, // A client-side API token OR one of your proxy's designated client keys (previously known as proxy secrets)
  refreshInterval: 15, // How often (in seconds) the client should poll the proxy for updates
  appName: "sysigreja", // The name of your application. It's only used for identifying your application
};

/**
 * O CSS de entrada chega como preload (ver asyncEntryCss no vite.config) para
 * não bloquear a splash. Antes do primeiro render, espera ele aplicar, para a
 * tela não piscar sem estilo. Teto de 4 s para nunca travar o boot.
 */
function aguardarCssDoApp(): Promise<void> {
  const pendentes = Array.from(
    document.querySelectorAll<HTMLLinkElement>("link[data-app-css]"),
  ).filter((link) => !link.sheet);
  if (pendentes.length === 0) return Promise.resolve();
  return new Promise((resolve) => {
    let restantes = pendentes.length;
    const teto = window.setTimeout(resolve, 4000);
    for (const link of pendentes) {
      const verificar = (event: Event) => {
        // O 1º load é o do preload; o rel vira stylesheet e o 2º chega já aplicado.
        if (event.type === "load" && !link.sheet) return;
        link.removeEventListener("load", verificar);
        link.removeEventListener("error", verificar);
        restantes -= 1;
        if (restantes === 0) {
          window.clearTimeout(teto);
          resolve();
        }
      };
      link.addEventListener("load", verificar);
      link.addEventListener("error", verificar);
    }
  });
}

/** Espera o idioma no máximo 3 s: se o chunk de tradução travar, a splash não fica presa. */
function aguardarI18n(): Promise<void> {
  return new Promise((resolve) => {
    const teto = window.setTimeout(resolve, 3000);
    i18nPronto
      .catch(() => undefined)
      .then(() => {
        window.clearTimeout(teto);
        resolve();
      });
  });
}

// Idioma escolhido (en/es) vem sob demanda: espera junto com o CSS para não piscar em português.
void Promise.all([aguardarCssDoApp(), aguardarI18n()]).then(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <BrowserRouter>
      <NuqsAdapter>
        <Provider store={store}>
          <PerformanceProvider>
            <SheetStackProvider>
              <Toaster
                expand
                visibleToasts={10}
                duration={3500}
                position={"top-right"}
              />
              <TooltipProvider delayDuration={100}>
                <FlagProvider config={flagProvider}>
                  <App />
                  <LeadWhatsappHost />
                </FlagProvider>
              </TooltipProvider>
            </SheetStackProvider>
          </PerformanceProvider>
        </Provider>
      </NuqsAdapter>
    </BrowserRouter>,
  );

  requestAnimationFrame(() => {
    document.querySelector(".pwa-splash")?.remove();
  });

  iniciarWebVitals();
  iniciarTrackingNovaVersao();
});
